import { useCallback, useRef, useState } from 'react';

/**
 * Cursor-based paginated fetching with infinite loading.
 *
 * `fetchPage` is called as `({ args, cursor, dateField, limit })` and must
 * resolve `{ items, hasMore, nextCursor, dateField, meta }` — `meta` carries
 * whatever only the first page returns (aggregates, totals, counts). It should
 * throw on failure.
 *
 * `reset(args)` starts over from page 1 with a new query; `args` is opaque to
 * the hook and handed back to every `fetchPage` call, so it acts as the query
 * key. A reset invalidates any in-flight request (generation counter), which is
 * what keeps rapid filter changes from interleaving stale pages.
 */
export const useCursorPagination = (fetchPage, { limit = 200 } = {}) => {
	const [state, setState] = useState({
		items: null,
		meta: null,
		hasMore: false,
		isLoading: false,
		isLoadingMore: false,
		error: null,
	});

	const argsRef = useRef(null);
	const cursorRef = useRef(null);
	const dateFieldRef = useRef(undefined);
	const itemsRef = useRef(null);
	const generationRef = useRef(0);
	const busyRef = useRef(false);

	const fetchPageRef = useRef(fetchPage);
	fetchPageRef.current = fetchPage;
	const limitRef = useRef(limit);
	limitRef.current = limit;

	const reset = useCallback(async args => {
		const generation = ++generationRef.current;
		argsRef.current = args;
		cursorRef.current = null;
		dateFieldRef.current = undefined;
		busyRef.current = true;
		setState(s => ({ ...s, isLoading: true, error: null }));
		try {
			const page = await fetchPageRef.current({
				args,
				cursor: null,
				dateField: undefined,
				limit: limitRef.current,
			});
			if (generation !== generationRef.current) {
				return false;
			}
			cursorRef.current = page.hasMore ? page.nextCursor : null;
			dateFieldRef.current = page.dateField;
			itemsRef.current = page.items || [];
			setState({
				items: itemsRef.current,
				meta: page.meta ?? null,
				hasMore: !!page.hasMore,
				isLoading: false,
				isLoadingMore: false,
				error: null,
			});
			return true;
		} catch (error) {
			if (generation !== generationRef.current) {
				return false;
			}
			setState(s => ({ ...s, isLoading: false, isLoadingMore: false, error }));
			return false;
		} finally {
			if (generation === generationRef.current) {
				busyRef.current = false;
			}
		}
	}, []);

	const loadMore = useCallback(async () => {
		// cursorRef doubles as the hasMore signal: null once exhausted (or before
		// the first reset). busyRef serializes with reset/fetchRest, so the scroll
		// sentinel may call this as often as it likes.
		if (busyRef.current || !cursorRef.current) {
			return;
		}
		const generation = generationRef.current;
		busyRef.current = true;
		setState(s => ({ ...s, isLoadingMore: true }));
		try {
			const page = await fetchPageRef.current({
				args: argsRef.current,
				cursor: cursorRef.current,
				dateField: dateFieldRef.current,
				limit: limitRef.current,
			});
			if (generation !== generationRef.current) {
				return;
			}
			cursorRef.current = page.hasMore ? page.nextCursor : null;
			itemsRef.current = [...(itemsRef.current || []), ...(page.items || [])];
			setState(s => ({
				...s,
				items: itemsRef.current,
				hasMore: !!page.hasMore,
				isLoadingMore: false,
			}));
		} catch (error) {
			if (generation !== generationRef.current) {
				return;
			}
			setState(s => ({ ...s, isLoadingMore: false, error }));
		} finally {
			if (generation === generationRef.current) {
				busyRef.current = false;
			}
		}
	}, []);

	// Fetches every remaining page and resolves the FULL item list (loaded +
	// rest) without touching state — a complete export must not dump thousands
	// of rows into the table. Throws if a reset lands mid-walk.
	const fetchRest = useCallback(async ({ limit: restLimit } = {}) => {
		const generation = generationRef.current;
		const all = [...(itemsRef.current || [])];
		let cursor = cursorRef.current;
		busyRef.current = true;
		try {
			while (cursor) {
				const page = await fetchPageRef.current({
					args: argsRef.current,
					cursor,
					dateField: dateFieldRef.current,
					limit: restLimit || limitRef.current,
				});
				if (generation !== generationRef.current) {
					throw new Error('query changed while fetching');
				}
				all.push(...(page.items || []));
				cursor = page.hasMore ? page.nextCursor : null;
			}
			return all;
		} finally {
			if (generation === generationRef.current) {
				busyRef.current = false;
			}
		}
	}, []);

	// Local edit of loaded state (e.g. removing a deleted row) — updater gets
	// `{ items, meta }` and returns the next `{ items, meta }`.
	const mutate = useCallback(updater => {
		setState(s => {
			if (!s.items) {
				return s;
			}
			const next = updater({ items: s.items, meta: s.meta });
			itemsRef.current = next.items ?? s.items;
			return {
				...s,
				items: itemsRef.current,
				meta: next.meta !== undefined ? next.meta : s.meta,
			};
		});
	}, []);

	return { ...state, reset, loadMore, fetchRest, mutate };
};
